import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Search, FileText, Mail, CalendarDays, Sparkles, ArrowRight, Check } from 'lucide-react';

/* ----------------------------------------------------------------------------
 * Solutions — marketing page, per audience/use-case. Neutral + single
 * signal-green accent, editorial serif headings, hairline borders. No purple.
 * Matches Home.js so the marketing site reads as one system.
 * -------------------------------------------------------------------------- */

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
};

const GREEN = '#2563EB';

const solutions = [
  {
    id: 'real-work',
    title: 'Real-Work Screen',
    icon: Search,
    headline: 'A task that mirrors the actual job.',
    problem: 'Whiteboard puzzles and trivia tell you almost nothing about how someone works.',
    solution: 'Spin up a role-specific, AI-allowed assessment in minutes. Candidates do real, messy work the way they would on the job.',
    features: ['Templates for common eng roles', 'AI explicitly allowed', 'Author in under ten minutes'],
  },
  {
    id: 'scoring',
    title: 'One Explainable Score',
    icon: FileText,
    headline: 'Scored in code, not by a model.',
    problem: 'Black-box scores you can\'t explain to a candidate — or an auditor — erode trust.',
    solution: 'A single 0–100 score is computed in code from your rubric, with the reasoning and evidence attached to every result.',
    features: ['Per-criterion point breakdown', 'Reasoning and evidence shown', 'Resistant to prompt injection'],
  },
  {
    id: 'proof-of-human',
    title: 'Proof of Human',
    icon: Mail,
    headline: 'A real person behind the work.',
    problem: 'With AI everywhere, you can no longer assume there\'s a genuine candidate on the other side.',
    solution: 'Server-observed behavioral signals — paste vs. typed, timing, environment — produce a plain verified-human state. No biometrics.',
    features: ['Behavioral signals only', 'Verified / needs review / flagged', 'Server-attributed, never self-reported'],
  },
  {
    id: 'audit',
    title: 'Audit & Compliance',
    icon: CalendarDays,
    headline: 'Explainable and logged by default.',
    problem: 'New hiring-AI rules demand decisions you can show your work on.',
    solution: 'Every result carries a full, exportable audit trail — built with LL-144 and the Mobley v. Workday standard in mind.',
    features: ['Exportable audit trail', 'Logged from day one', 'Behavioral signals, no biometrics'],
  },
];

/* Small product-preview card echoing the in-app screens. */
function SolutionPreview({ Icon }) {
  return (
    <div className="relative w-full max-w-md">
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_24px_48px_-24px_rgba(15,23,42,0.18)] p-5 sm:p-6">
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-4">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: GREEN }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: GREEN }} />
            Live preview
          </div>
          <div className="flex h-8 w-8 items-center justify-center rounded-full" style={{ background: '#EFF4FE', color: GREEN }}>
            <Icon size={15} strokeWidth={1.8} />
          </div>
        </div>
        <div className="mt-5 space-y-3">
          <div className="h-3 w-3/4 rounded-full bg-gray-100 dark:bg-gray-800" />
          <div className="h-3 w-1/2 rounded-full bg-gray-100 dark:bg-gray-800" />
          <div className="mt-4 rounded-xl border border-gray-100 dark:border-gray-800 p-4">
            {[88, 64].map((w, i) => (
              <div key={i} className="mb-3 last:mb-0">
                <div className="mb-1.5 h-2.5 rounded-full bg-gray-100 dark:bg-gray-800" style={{ width: `${w}%` }} />
                <div className="h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ background: GREEN }}
                    initial={{ width: 0 }}
                    whileInView={{ width: i === 0 ? '92%' : '74%' }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, delay: 0.15 * i, ease: 'easeOut' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Floating verified-human chip */}
      <motion.div
        {...reveal}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="absolute -bottom-4 -right-3 flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_18px_36px_-20px_rgba(15,23,42,0.22)]"
      >
        <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: '#EFF4FE', color: GREEN }}>
          <Sparkles size={16} strokeWidth={1.8} />
        </div>
        <div>
          <div className="text-[11px] text-gray-500">Proof of human</div>
          <div className="text-sm font-semibold text-gray-900 dark:text-white">Verified ✓</div>
        </div>
      </motion.div>
    </div>
  );
}

function Solutions() {
  return (
    <div className="bg-[#F8FAFC] dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      {/* ===== HERO ===== */}
      <section className="border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-white to-[#F8FAFC] dark:from-gray-950 dark:to-gray-950">
        <div className="mx-auto max-w-7xl px-6 py-20 text-center lg:px-8 lg:py-28">
          <motion.div {...reveal} transition={{ duration: 0.5 }} className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.05em] text-gray-500">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: GREEN }} />
            Solutions · per use-case
          </motion.div>
          <motion.h1 {...reveal} transition={{ duration: 0.55, delay: 0.05 }}
            className="mx-auto mt-6 max-w-3xl font-serif text-5xl font-semibold leading-[1.05] tracking-tight text-gray-900 dark:text-white sm:text-6xl">
            Solutions for modern hiring
          </motion.h1>
          <motion.p {...reveal} transition={{ duration: 0.55, delay: 0.1 }}
            className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-gray-600 dark:text-gray-300">
            Every part of the workflow points at one outcome: real work, a real person, and one score you can actually explain.
          </motion.p>
        </div>
      </section>

      {/* ===== SOLUTIONS ===== */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
        <div className="grid grid-cols-1 gap-16 lg:gap-20">
          {solutions.map((item, index) => {
            const Icon = item.icon;
            const flip = index % 2 === 1;
            return (
              <motion.div
                key={item.id}
                {...reveal}
                transition={{ duration: 0.5 }}
                className="overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 transition-colors hover:border-gray-300 dark:hover:border-gray-700"
              >
                <div className={`flex flex-col lg:flex-row ${flip ? 'lg:flex-row-reverse' : ''}`}>
                  {/* Copy side */}
                  <div className="flex flex-col justify-center p-8 lg:w-1/2 lg:p-12">
                    <div className="inline-flex w-fit items-center gap-2.5 rounded-full px-3 py-1.5 text-xs font-semibold" style={{ background: '#EFF4FE', color: '#1D4FD8', border: '1px solid #BFDBFE' }}>
                      <Icon size={15} strokeWidth={1.8} />
                      {item.title}
                    </div>

                    <h2 className="mt-6 font-serif text-3xl font-semibold tracking-tight text-gray-900 dark:text-white md:text-4xl">
                      {item.headline}
                    </h2>

                    <div className="my-7 space-y-6">
                      <div>
                        <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-400">The problem</h4>
                        <p className="leading-relaxed text-gray-600 dark:text-gray-300">{item.problem}</p>
                      </div>
                      <div>
                        <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: GREEN }}>The solution</h4>
                        <p className="leading-relaxed text-gray-600 dark:text-gray-300">{item.solution}</p>
                      </div>
                    </div>

                    <ul className="mb-8 space-y-2.5">
                      {item.features.map((feat, i) => (
                        <li key={i} className="flex items-center gap-2.5 text-sm text-gray-600 dark:text-gray-300">
                          <Check size={16} style={{ color: GREEN }} /> {feat}
                        </li>
                      ))}
                    </ul>

                    <Link
                      to="/register"
                      className="group inline-flex w-fit items-center gap-2 text-sm font-semibold text-gray-900 transition-colors dark:text-white"
                      onMouseEnter={(e) => (e.currentTarget.style.color = GREEN)}
                      onMouseLeave={(e) => (e.currentTarget.style.color = '')}
                    >
                      Try {item.title}
                      <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </div>

                  {/* Visual side */}
                  <div className="relative flex min-h-[360px] items-center justify-center border-t border-gray-200 bg-[#F8FAFC] p-8 dark:border-gray-800 dark:bg-gray-950 lg:min-h-0 lg:border-t-0">
                    <div className={`absolute inset-y-0 hidden w-px bg-gray-200 dark:bg-gray-800 lg:block ${flip ? 'right-0' : 'left-0'}`} />
                    <SolutionPreview Icon={Icon} />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="bg-[#0F172A]">
        <div className="mx-auto max-w-5xl px-6 py-24 text-center lg:px-8">
          <motion.div {...reveal} transition={{ duration: 0.55 }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-gray-300">
              <Sparkles size={14} style={{ color: GREEN }} /> One platform, every stage of the funnel
            </div>
            <h2 className="mt-7 font-serif text-4xl font-semibold leading-tight text-white sm:text-5xl">
              Put the right agent on the job.
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-gray-300">
              Source, screen, reach out, and schedule — each step automated where it helps and transparent where it counts.
            </p>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link to="/register" className="inline-flex items-center justify-center gap-2 rounded-lg px-7 py-3.5 text-base font-semibold text-white" style={{ background: GREEN }}>
                Get started <ArrowRight size={17} />
              </Link>
              <Link to="/contact" className="inline-flex items-center justify-center rounded-lg border border-white/20 bg-transparent px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-white/10">
                Talk to sales
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default Solutions;
